import { isPresent } from "@mixitone/util";
import {
  assignmentCost,
  bestDoublesOrder,
  buildRepeatScorer,
  canCoexist,
  compareCost,
  countUnitCombinations,
  gameGroupImbalance,
  isValidAssignment,
  scoreLevelBalanceForGame,
  type RepeatScorer,
} from "./cost";
import { createRng, seededShuffle } from "./rng";
import type { Assignment, FillContext } from "./types";

const MAX_CANDIDATES = 50;
const MAX_SWEEPS = 20;
const MAX_MOVES_PER_SWEEP = 2000;

interface MutableState {
  games: Map<number, Array<string | undefined>>;
  benchUnits: string[][];
  fixedByGame: Map<number, Array<string | undefined>>;
}

type Move =
  | { type: "bench_swap"; gameIndex: number; benchUnitIndex: number; seatedUnit: string[] }
  | { type: "cross_swap"; gameIndexA: number; unitA: string[]; gameIndexB: number; unitB: string[] }
  | { type: "bench_to_game"; gameIndex: number; benchUnitIndex: number };

function seatedPlayers(slots: Array<string | undefined>): string[] {
  return slots.filter((id): id is string => isPresent(id));
}

function fixedCount(fixed: Array<string | undefined>): number {
  return fixed.filter(isPresent).length;
}

function hasFixedPlayers(fixed: Array<string | undefined>): boolean {
  return fixed.some(isPresent);
}

function buildUnits(ctx: FillContext, playerIds: string[]): string[][] {
  const available = new Set(playerIds);
  const counted = new Set<string>();
  const units: string[][] = [];

  for (const playerId of playerIds) {
    if (counted.has(playerId)) continue;
    const partnerId = ctx.playerPairs[playerId];
    if (partnerId && available.has(partnerId)) {
      counted.add(partnerId);
      units.push([playerId, partnerId]);
    } else {
      units.push([playerId]);
    }
  }

  return units;
}

function enumerateUnitCombinations(units: string[][], n: number): string[][] {
  const results: string[][] = [];

  const enumerate = (unitIndex: number, selected: string[], size: number) => {
    if (size === n) {
      results.push([...selected]);
      return;
    }
    if (size > n || unitIndex >= units.length) return;

    const unit = units[unitIndex];
    if (size + unit.length <= n) {
      enumerate(unitIndex + 1, [...selected, ...unit], size + unit.length);
    }
    enumerate(unitIndex + 1, selected, size);
  };

  enumerate(0, [], 0);
  return results;
}

function sampleUnitCombinations(
  units: string[][],
  n: number,
  maxSamples: number,
  rng: () => number,
): string[][] {
  const seen = new Set<string>();
  const results: string[][] = [];
  let attempts = 0;
  const maxAttempts = maxSamples * 10;

  while (results.length < maxSamples && attempts < maxAttempts) {
    attempts++;
    const shuffled = seededShuffle(rng, units);
    const selected: string[] = [];
    let size = 0;

    for (const unit of shuffled) {
      if (size + unit.length <= n) {
        selected.push(...unit);
        size += unit.length;
        if (size === n) break;
      }
    }

    if (size !== n) continue;
    const key = [...selected].sort().join(",");
    if (seen.has(key)) continue;
    seen.add(key);
    results.push(selected);
  }

  return results;
}

function getUnitCombinations(units: string[][], n: number, rng: () => number): string[][] {
  if (n === 0) return [[]];
  const comboCount = countUnitCombinations(units, n);
  return comboCount <= MAX_CANDIDATES
    ? enumerateUnitCombinations(units, n)
    : sampleUnitCombinations(units, n, MAX_CANDIDATES, rng);
}

function benchPlayerCount(state: MutableState): number {
  return state.benchUnits.reduce((sum, unit) => sum + unit.length, 0);
}

function targetGameSize(ctx: FillContext, state: MutableState, currentSeated: number): number {
  if (ctx.mode === "singles_only") return 2;

  const remaining = benchPlayerCount(state) + currentSeated;
  if (ctx.mode === "doubles_and_singles" && remaining < 4 && currentSeated === 0) {
    return remaining >= 2 ? 2 : 0;
  }

  return 4;
}

function createInitialState(ctx: FillContext): MutableState {
  const fixedByGame = new Map(ctx.games.map((game) => [game.gameIndex, [...game.fixed]]));
  const games = new Map<number, Array<string | undefined>>();
  const fixedPlayers = new Set<string>();

  for (const game of ctx.games) {
    games.set(
      game.gameIndex,
      game.fixed.map((playerId) => {
        if (isPresent(playerId)) fixedPlayers.add(playerId);
        return playerId;
      }),
    );
  }

  const movablePlayers = ctx.playerIds.filter((id) => !fixedPlayers.has(id));
  return {
    games,
    benchUnits: buildUnits(ctx, movablePlayers),
    fixedByGame,
  };
}

function removeUnitsFromBench(state: MutableState, units: string[][]): void {
  for (const unit of units) {
    const index = state.benchUnits.findIndex(
      (benchUnit) => benchUnit.length === unit.length && benchUnit.every((id) => unit.includes(id)),
    );
    if (index !== -1) state.benchUnits.splice(index, 1);
  }
}

function addUnitToBench(state: MutableState, unit: string[]): void {
  state.benchUnits.push([...unit]);
}

function placePlayersInOpenSlots(slots: Array<string | undefined>, players: string[]): void {
  let playerIndex = 0;
  for (let slot = 0; slot < slots.length && playerIndex < players.length; slot++) {
    if (!isPresent(slots[slot])) {
      slots[slot] = players[playerIndex++];
    }
  }
}

function removePlayersFromSlots(
  slots: Array<string | undefined>,
  fixed: Array<string | undefined>,
  playerIds: string[],
): void {
  const removeSet = new Set(playerIds);
  for (let slot = 0; slot < slots.length; slot++) {
    const playerId = slots[slot];
    if (isPresent(playerId) && removeSet.has(playerId) && !isPresent(fixed[slot])) {
      slots[slot] = undefined;
    }
  }
}

function candidateIsValid(ctx: FillContext, seated: string[], candidate: string[]): boolean {
  const combined = [...seated, ...candidate];
  for (const playerId of candidate) {
    const others = combined.filter((id) => id !== playerId);
    if (!canCoexist(ctx, playerId, others)) return false;
  }
  return true;
}

function rankCandidate(
  ctx: FillContext,
  seated: string[],
  candidate: string[],
  repeatScore: RepeatScorer,
  satOutLastSet: Set<string>,
  order: number,
) {
  const combined = [...seated, ...candidate];
  const satOutCount = candidate.filter((id) => satOutLastSet.has(id)).length;
  const timesOutSum = candidate.reduce((sum, id) => sum + (ctx.timesOut[id] ?? 0), 0);

  const groupSpread = new Set(combined.map((id) => ctx.playerGroups[id]).filter(isPresent)).size;

  return {
    satOutCount,
    negTimesOut: -timesOutSum,
    groupImbalance: gameGroupImbalance(ctx, combined),
    groupSpread,
    levelScore: scoreLevelBalanceForGame(combined, ctx.playerLevels),
    repeatScore: repeatScore(combined),
    order,
    players: candidate,
  };
}

function compareCandidates(a: ReturnType<typeof rankCandidate>, b: ReturnType<typeof rankCandidate>): number {
  if (a.satOutCount !== b.satOutCount) return b.satOutCount - a.satOutCount;
  if (a.negTimesOut !== b.negTimesOut) return a.negTimesOut - b.negTimesOut;
  if (a.groupImbalance !== b.groupImbalance) return a.groupImbalance - b.groupImbalance;
  if (a.groupSpread !== b.groupSpread) return a.groupSpread - b.groupSpread;
  if (a.levelScore !== b.levelScore) return a.levelScore - b.levelScore;
  if (a.repeatScore !== b.repeatScore) return a.repeatScore - b.repeatScore;
  return a.order - b.order;
}

function eligibleBenchUnits(ctx: FillContext, state: MutableState, seated: string[]): string[][] {
  return state.benchUnits.filter((unit) => candidateIsValid(ctx, seated, unit));
}

function fillGameConstructive(
  ctx: FillContext,
  state: MutableState,
  gameIndex: number,
  repeatScore: RepeatScorer,
  satOutLastSet: Set<string>,
  rng: () => number,
): void {
  const slots = state.games.get(gameIndex)!;
  const seated = seatedPlayers(slots);
  const needed = targetGameSize(ctx, state, seated.length) - seated.length;
  if (needed <= 0) return;

  const units = eligibleBenchUnits(ctx, state, seated);
  const combos = getUnitCombinations(units, needed, rng);
  let best: ReturnType<typeof rankCandidate> | undefined;
  let order = 0;

  for (const combo of combos) {
    if (!candidateIsValid(ctx, seated, combo)) continue;
    const ranked = rankCandidate(ctx, seated, combo, repeatScore, satOutLastSet, order++);
    if (!best || compareCandidates(ranked, best) < 0) best = ranked;
  }

  if (!best) return;

  const chosenUnits = buildUnits(ctx, best.players);
  removeUnitsFromBench(state, chosenUnits);
  placePlayersInOpenSlots(slots, best.players);
}

function constructivePass(
  ctx: FillContext,
  state: MutableState,
  repeatScore: RepeatScorer,
  satOutLastSet: Set<string>,
  rng: () => number,
): void {
  const gameOrder = ctx.games
    .slice()
    .sort((a, b) => fixedCount(b.fixed) - fixedCount(a.fixed) || a.gameIndex - b.gameIndex);

  for (const game of gameOrder) {
    fillGameConstructive(ctx, state, game.gameIndex, repeatScore, satOutLastSet, rng);
  }
}

function toAssignment(ctx: FillContext, state: MutableState): Assignment {
  const bench = state.benchUnits.flat();
  const games = ctx.games.map((game) => ({
    gameIndex: game.gameIndex,
    slots: [...state.games.get(game.gameIndex)!],
  }));
  return { games, bench };
}

function applyBestDoublesOrder(ctx: FillContext, state: MutableState): void {
  for (const game of ctx.games) {
    if (hasFixedPlayers(game.fixed)) continue;
    const slots = state.games.get(game.gameIndex)!;
    const players = seatedPlayers(slots);
    if (players.length !== 4) continue;
    const ordered = bestDoublesOrder(players, ctx.playerLevels);
    for (let slot = 0; slot < 4; slot++) {
      slots[slot] = ordered[slot];
    }
  }
}

function seatedUnitsInGame(
  ctx: FillContext,
  slots: Array<string | undefined>,
  fixed: Array<string | undefined>,
): string[][] {
  const fixedSet = new Set(fixed.filter(isPresent));
  const movable = seatedPlayers(slots).filter((id) => !fixedSet.has(id));
  return buildUnits(ctx, movable);
}

function applyMove(state: MutableState, move: Move): void {
  switch (move.type) {
    case "bench_swap": {
      const slots = state.games.get(move.gameIndex)!;
      const fixed = state.fixedByGame.get(move.gameIndex)!;
      const benchUnit = state.benchUnits[move.benchUnitIndex];
      if (!benchUnit || benchUnit.length !== move.seatedUnit.length) return;
      removePlayersFromSlots(slots, fixed, move.seatedUnit);
      state.benchUnits.splice(move.benchUnitIndex, 1);
      addUnitToBench(state, move.seatedUnit);
      placePlayersInOpenSlots(slots, benchUnit);
      break;
    }
    case "cross_swap": {
      const slotsA = state.games.get(move.gameIndexA)!;
      const slotsB = state.games.get(move.gameIndexB)!;
      const fixedA = state.fixedByGame.get(move.gameIndexA)!;
      const fixedB = state.fixedByGame.get(move.gameIndexB)!;
      removePlayersFromSlots(slotsA, fixedA, move.unitA);
      removePlayersFromSlots(slotsB, fixedB, move.unitB);
      placePlayersInOpenSlots(slotsA, move.unitB);
      placePlayersInOpenSlots(slotsB, move.unitA);
      break;
    }
    case "bench_to_game": {
      const unit = state.benchUnits[move.benchUnitIndex];
      if (!unit) return;
      const slots = state.games.get(move.gameIndex)!;
      state.benchUnits.splice(move.benchUnitIndex, 1);
      placePlayersInOpenSlots(slots, unit);
      break;
    }
  }
}

function enumerateMoves(ctx: FillContext, state: MutableState, rng: () => number, lite = false): Move[] {
  const moves: Move[] = [];
  const gameIndices = [...state.games.keys()];

  for (const gameIndex of gameIndices) {
    const slots = state.games.get(gameIndex)!;
    const fixed = state.fixedByGame.get(gameIndex)!;
    const seated = seatedPlayers(slots);
    const seatedUnits = seatedUnitsInGame(ctx, slots, fixed);
    const openSeats = slots.filter((id) => !isPresent(id)).length;

    for (let benchIndex = 0; benchIndex < state.benchUnits.length; benchIndex++) {
      const benchUnit = state.benchUnits[benchIndex];

      for (const seatedUnit of seatedUnits) {
        if (benchUnit.length !== seatedUnit.length) continue;
        moves.push({
          type: "bench_swap",
          gameIndex,
          benchUnitIndex: benchIndex,
          seatedUnit: [...seatedUnit],
        });
      }

      if (openSeats >= benchUnit.length && candidateIsValid(ctx, seated, benchUnit)) {
        moves.push({ type: "bench_to_game", gameIndex, benchUnitIndex: benchIndex });
      }
    }
  }

  const benchEmpty = state.benchUnits.length === 0;
  if (lite && !benchEmpty) return seededShuffle(rng, moves);

  for (let i = 0; i < gameIndices.length; i++) {
    for (let j = i + 1; j < gameIndices.length; j++) {
      const gameIndexA = gameIndices[i];
      const gameIndexB = gameIndices[j];
      const slotsA = state.games.get(gameIndexA)!;
      const slotsB = state.games.get(gameIndexB)!;
      const fixedA = state.fixedByGame.get(gameIndexA)!;
      const fixedB = state.fixedByGame.get(gameIndexB)!;
      const unitsA = seatedUnitsInGame(ctx, slotsA, fixedA);
      const unitsB = seatedUnitsInGame(ctx, slotsB, fixedB);

      for (const unitA of unitsA) {
        for (const unitB of unitsB) {
          if (unitA.length !== unitB.length) continue;
          moves.push({
            type: "cross_swap",
            gameIndexA,
            unitA: [...unitA],
            gameIndexB,
            unitB: [...unitB],
          });
        }
      }
    }
  }

  const shuffled = seededShuffle(rng, moves);
  return shuffled.length > MAX_MOVES_PER_SWEEP ? shuffled.slice(0, MAX_MOVES_PER_SWEEP) : shuffled;
}

function snapshotState(state: MutableState): MutableState {
  return {
    games: new Map([...state.games.entries()].map(([index, slots]) => [index, [...slots]])),
    benchUnits: state.benchUnits.map((unit) => [...unit]),
    fixedByGame: state.fixedByGame,
  };
}

function restoreState(state: MutableState, snapshot: MutableState): void {
  state.games = new Map([...snapshot.games.entries()].map(([index, slots]) => [index, [...slots]]));
  state.benchUnits = snapshot.benchUnits.map((unit) => [...unit]);
}

function isFixedPlayer(ctx: FillContext, playerId: string): boolean {
  return ctx.games.some((game) => game.fixed.includes(playerId));
}

/** Greedy fairness pass; only accepts moves that strictly improve total cost. */
function repairFairness(ctx: FillContext, state: MutableState, repeatScore: RepeatScorer): void {
  let currentCost = assignmentCost(ctx, toAssignment(ctx, state), repeatScore);
  let changed = true;
  let iterations = 0;

  while (changed && iterations++ < 50) {
    changed = false;
    for (const gameIndex of state.games.keys()) {
      const slots = state.games.get(gameIndex)!;
      const fixed = state.fixedByGame.get(gameIndex)!;
      const seatedUnits = seatedUnitsInGame(ctx, slots, fixed);

      for (let benchIndex = 0; benchIndex < state.benchUnits.length; benchIndex++) {
        const benchUnit = state.benchUnits[benchIndex];
        for (const seatedUnit of seatedUnits) {
          if (benchUnit.length !== seatedUnit.length) continue;

          const benchTimesOut = Math.min(...benchUnit.map((id) => ctx.timesOut[id] ?? 0));
          const seatedTimesOut = Math.max(...seatedUnit.map((id) => ctx.timesOut[id] ?? 0));
          if (benchTimesOut <= seatedTimesOut) continue;
          if (seatedUnit.some((id) => isFixedPlayer(ctx, id))) continue;

          const saved = snapshotState(state);
          applyMove(state, {
            type: "bench_swap",
            gameIndex,
            benchUnitIndex: benchIndex,
            seatedUnit: [...seatedUnit],
          });
          const assignment = toAssignment(ctx, state);
          if (!isValidAssignment(ctx, assignment)) {
            restoreState(state, saved);
            continue;
          }

          const nextCost = assignmentCost(ctx, assignment, repeatScore);
          if (compareCost(nextCost, currentCost) < 0) {
            currentCost = nextCost;
            changed = true;
            break;
          }
          restoreState(state, saved);
        }
        if (changed) break;
      }
      if (changed) break;
    }
  }
}

function searchLimits(ctx: FillContext): { maxSweeps: number; maxMoves: number; lite: boolean } {
  const n = ctx.playerIds.length;
  if (n > 40) return { maxSweeps: 3, maxMoves: 400, lite: true };
  if (n > 20) return { maxSweeps: 8, maxMoves: 1000, lite: false };
  return { maxSweeps: MAX_SWEEPS, maxMoves: MAX_MOVES_PER_SWEEP, lite: false };
}

function localSearch(
  ctx: FillContext,
  state: MutableState,
  repeatScore: RepeatScorer,
  rng: () => number,
): void {
  const { maxSweeps, maxMoves, lite } = searchLimits(ctx);
  let currentCost = assignmentCost(ctx, toAssignment(ctx, state), repeatScore);

  for (let sweep = 0; sweep < maxSweeps; sweep++) {
    let improved = false;
    const moves = enumerateMoves(ctx, state, rng, lite).slice(0, maxMoves);

    for (const move of moves) {
      const saved = snapshotState(state);
      applyMove(state, move);
      const assignment = toAssignment(ctx, state);
      if (!isValidAssignment(ctx, assignment)) {
        restoreState(state, saved);
        continue;
      }

      const nextCost = assignmentCost(ctx, assignment, repeatScore);
      if (compareCost(nextCost, currentCost) < 0) {
        currentCost = nextCost;
        improved = true;
      } else {
        restoreState(state, saved);
      }
    }

    if (!improved) break;
  }
}

export function solve(ctx: FillContext): Assignment {
  const rng = createRng(ctx.seed);
  const repeatScore = buildRepeatScorer(ctx.previousSets, ctx.currentSetIndex);
  const satOutLastSet = new Set(ctx.satOutLastSet);
  const state = createInitialState(ctx);
  constructivePass(ctx, state, repeatScore, satOutLastSet, rng);
  repairFairness(ctx, state, repeatScore);
  localSearch(ctx, state, repeatScore, rng);
  applyBestDoublesOrder(ctx, state);
  return toAssignment(ctx, state);
}
